import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import type { Currency, SaleStatus } from '@shared/contracts/enums'
import type {
  SaleDebtApplication,
  SaleDetail,
  SaleFinancialEvent,
  SaleSplitPart,
  SaleSplitRole,
} from './sale.contract'

export interface JourneyItem {
  kind: string
  productName: string
  offerName?: string
  amount: MonetaryValue
  platformFee?: MonetaryValue
  currencyConversionFee?: MonetaryValue
  fixedFee?: MonetaryValue
  reserveAmount?: MonetaryValue
  netAmount?: MonetaryValue
  youCommissioned?: boolean
  separateCharge: boolean
  billingType?: 'ONE_TIME' | 'RECURRING'
  status?: SaleStatus
  handledByGlobalChargeback?: boolean
  financialEvents?: SaleFinancialEvent[]
  subscriptionId?: string
  subscriptionStatus?: SaleDetail['subscriptionStatus']
}

export interface JourneyDeduction {
  id: string
  type: 'fee' | 'currencyConversion' | 'participant' | 'stage'
  role?: SaleSplitRole
  kind?: string
  productName?: string
  amount: MonetaryValue
  available?: MonetaryValue
  reserve?: MonetaryValue
}

export interface JourneyFinancials {
  commercialAmount?: MonetaryValue
  fxVariation?: MonetaryValue
  items: JourneyItem[]
  embeddedUncommissionedItems: JourneyItem[]
  totalPaid: MonetaryValue
  totalGross: MonetaryValue
  converted: boolean
  deductions: JourneyDeduction[]
  fixedFeeCount?: number
  netTotal: MonetaryValue
  availableNow: MonetaryValue
  reserveHeld: MonetaryValue
  hasReserve: boolean
  debtConsumed: MonetaryValue
  remainingReceivable: MonetaryValue
  remainingReserve: MonetaryValue
  hasDebtConsumption: boolean
  availableDates: string[]
  awaitingAvailability: boolean
  availabilityConfirmed: boolean
}

interface Charge {
  gross: MonetaryValue
  fee: MonetaryValue
  fixedFee?: MonetaryValue
  currencyConversionFee: MonetaryValue
  net: MonetaryValue
  reserve: MonetaryValue
  split: SaleSplitPart[]
  debtApplication?: SaleDebtApplication
  availableAt?: string
  availability?: SaleDetail['availability']
  kind?: string
  productName?: string
  offerName?: string
}

const ROLE_ORDER: Record<SaleSplitRole, number> = {
  PRODUCER: 0,
  COPRODUCER: 1,
  AFFILIATE: 2,
  SEED: 3,
}

function sumMoney(values: MonetaryValue[], currency: Currency): Money {
  let total = new Money(0, currency)
  for (const value of values) {
    if (value.currency !== currency) {
      continue
    }
    total = total.add(Money.fromMonetaryValue(value))
  }
  return total
}

function zeroMoney(currency: Currency): MonetaryValue {
  return { amount: '0.000', currency }
}

function feeWithoutConversion(charge: Charge): MonetaryValue {
  return Money.fromMonetaryValue(charge.fee)
    .subtract(Money.fromMonetaryValue(charge.currencyConversionFee))
    .toMonetaryValue()
}

function chargesFrom(parent: SaleDetail): { parent: Charge; stages: Charge[] } {
  const parentCharge: Charge = {
    gross: parent.grossAmount,
    fee: parent.platformFee,
    fixedFee: parent.fixedFee,
    currencyConversionFee: parent.currencyConversionFee ?? zeroMoney(parent.grossAmount.currency),
    net: parent.netAmount,
    reserve: parent.reserveAmount,
    split: parent.split,
    debtApplication: parent.debtApplication,
    availableAt: parent.availableAt,
    availability: parent.availability,
  }
  const stages: Charge[] = (parent.funnelStages ?? []).map((stage) => ({
    gross: stage.grossAmount,
    fee: stage.platformFee,
    fixedFee: stage.fixedFee,
    currencyConversionFee: stage.currencyConversionFee ?? zeroMoney(stage.grossAmount.currency),
    net: stage.netAmount,
    reserve: stage.reserveAmount,
    split: stage.split,
    debtApplication: stage.debtApplication,
    availableAt: stage.availableAt,
    availability: stage.availability,
    kind: stage.kind,
    productName: stage.productName,
    offerName: stage.offerName,
  }))
  return { parent: parentCharge, stages }
}

function compositionItems(parent: SaleDetail): JourneyItem[] {
  const principal: JourneyItem[] =
    parent.lines && parent.lines.length > 0
      ? parent.lines.map((line) => ({
          kind: line.kind,
          productName: line.productName,
          offerName: line.offerName,
          amount: line.amount,
          platformFee: line.platformFee,
          currencyConversionFee: line.currencyConversionFee,
          fixedFee: line.fixedFee,
          reserveAmount: line.reserveAmount,
          netAmount: line.netAmount,
          youCommissioned: line.youCommissioned,
          separateCharge: false,
          // The line payload has no subscription contract for bumps.
          billingType:
            line.kind === 'PRINCIPAL'
              ? parent.subscriptionId
                ? 'RECURRING'
                : 'ONE_TIME'
              : undefined,
        }))
      : [
          {
            kind: 'PRINCIPAL',
            productName: parent.productName,
            offerName: parent.offerName,
            amount: parent.grossAmount,
            platformFee: parent.platformFee,
            currencyConversionFee: parent.currencyConversionFee,
            fixedFee: parent.fixedFee,
            reserveAmount: parent.reserveAmount,
            netAmount: parent.netAmount,
            youCommissioned: parent.split.some((part) => part.isYou),
            separateCharge: false,
            billingType: parent.subscriptionId ? 'RECURRING' : 'ONE_TIME',
          },
        ]

  const stages: JourneyItem[] = (parent.funnelStages ?? []).map((stage) => ({
    kind: stage.kind,
    productName: stage.productName,
    offerName: stage.offerName,
    amount: stage.grossAmount,
    platformFee: stage.platformFee,
    currencyConversionFee: stage.currencyConversionFee,
    fixedFee: stage.fixedFee,
    reserveAmount: stage.reserveAmount,
    netAmount: stage.netAmount,
    youCommissioned: stage.youCommissioned,
    separateCharge: true,
    billingType: stage.subscriptionId ? 'RECURRING' : 'ONE_TIME',
    status: stage.status === 'PAID' ? undefined : stage.status,
    handledByGlobalChargeback: stage.handledByGlobalChargeback,
    financialEvents: stage.financialEvents,
    subscriptionId: stage.subscriptionId,
    subscriptionStatus: stage.subscriptionStatus,
  }))

  return [...principal, ...stages]
}

export function buildJourneyFinancials(parent: SaleDetail): JourneyFinancials {
  const receiving = parent.grossAmount.currency
  const buyer = parent.amount.currency
  const { parent: parentCharge, stages } = chargesFrom(parent)
  const all = [parentCharge, ...stages]
  const items = compositionItems(parent)

  const sliceOf = (charge: Charge) => sumMoney([charge.net, charge.reserve], receiving)
  const commissioned = all.filter((charge) => !sliceOf(charge).isZero())
  const uncommissionedStages = stages.filter((charge) => sliceOf(charge).isZero())

  const totalGross = sumMoney(
    all.map((charge) => charge.gross),
    receiving,
  ).toMonetaryValue()
  const totalPaid = sumMoney(
    [parent.amount, ...(parent.funnelStages ?? []).map((stage) => stage.amount)],
    buyer,
  ).toMonetaryValue()
  const chargeSales = [parent, ...(parent.funnelStages ?? [])].filter(
    (charge) =>
      (charge.status !== 'FAILED' && charge.status !== 'PENDING') ||
      !Money.fromMonetaryValue(charge.grossAmount).isZero(),
  )
  const bases = chargeSales.map(
    (charge) =>
      charge.commercialAmount ??
      (charge.amount.currency === charge.grossAmount.currency ? charge.grossAmount : undefined),
  )
  const commercialAmount =
    bases.every((base) => base?.currency === receiving) &&
    chargeSales.some((charge) => charge.fxVariation !== undefined)
      ? sumMoney(bases as MonetaryValue[], receiving).toMonetaryValue()
      : undefined
  const fxVariation =
    commercialAmount === undefined
      ? undefined
      : Money.fromMonetaryValue(totalGross)
          .subtract(Money.fromMonetaryValue(commercialAmount))
          .toMonetaryValue()

  const deductions: JourneyDeduction[] = []
  const embeddedUncommissionedItems = items.filter(
    (item) => !item.separateCharge && item.youCommissioned === false,
  )

  const fee = sumMoney(commissioned.map(feeWithoutConversion), receiving)
  if (!fee.isZero()) {
    deductions.push({ id: 'fee', type: 'fee', amount: fee.toMonetaryValue() })
  }

  const currencyConversionFee = sumMoney(
    commissioned.map((charge) => charge.currencyConversionFee),
    receiving,
  )
  if (!currencyConversionFee.isZero()) {
    deductions.push({
      id: 'currency-conversion',
      type: 'currencyConversion',
      amount: currencyConversionFee.toMonetaryValue(),
    })
  }

  const byRole = new Map<SaleSplitRole, { amount: Money; available: Money; reserve: Money }>()
  for (const charge of commissioned) {
    for (const part of charge.split) {
      if (part.isYou) {
        continue
      }
      const acc = byRole.get(part.role) ?? {
        amount: new Money(0, receiving),
        available: new Money(0, receiving),
        reserve: new Money(0, receiving),
      }
      acc.amount = acc.amount.add(Money.fromMonetaryValue(part.amount))
      acc.available = acc.available.add(Money.fromMonetaryValue(part.available ?? part.amount))
      acc.reserve = acc.reserve.add(
        Money.fromMonetaryValue(part.reserve ?? { amount: '0', currency: receiving }),
      )
      byRole.set(part.role, acc)
    }
  }
  for (const [role, acc] of [...byRole.entries()].sort(
    (left, right) => ROLE_ORDER[left[0]] - ROLE_ORDER[right[0]],
  )) {
    deductions.push({
      id: `participant:${role}`,
      type: 'participant',
      role,
      amount: acc.amount.toMonetaryValue(),
      available: acc.available.toMonetaryValue(),
      reserve: acc.reserve.toMonetaryValue(),
    })
  }

  for (const stage of uncommissionedStages) {
    deductions.push({
      id: `stage:${stage.kind ?? 'UPSELL'}`,
      type: 'stage',
      kind: stage.kind,
      productName: stage.productName,
      amount: stage.gross,
    })
  }

  const availableNow = sumMoney(
    commissioned.map((charge) => charge.net),
    receiving,
  )
  const reserveHeld = sumMoney(
    commissioned.map((charge) => charge.reserve),
    receiving,
  )
  const netTotal = availableNow.add(reserveHeld)
  let debtConsumed = new Money(0, receiving)
  let remainingReceivable = new Money(0, receiving)
  let remainingReserve = new Money(0, receiving)
  let hasDebtConsumption = false
  for (const charge of commissioned) {
    const debt = charge.debtApplication
    if (!debt) {
      remainingReceivable = remainingReceivable.add(Money.fromMonetaryValue(charge.net))
      remainingReserve = remainingReserve.add(Money.fromMonetaryValue(charge.reserve))
      continue
    }
    hasDebtConsumption ||= debt.hasDebtConsumption
    debtConsumed = debtConsumed
      .add(Money.fromMonetaryValue(debt.consumedReceivableAmount))
      .add(Money.fromMonetaryValue(debt.consumedReserveAmount))
    remainingReceivable = remainingReceivable.add(
      Money.fromMonetaryValue(debt.remainingReceivableAmount),
    )
    remainingReserve = remainingReserve.add(Money.fromMonetaryValue(debt.remainingReserveAmount))
  }
  const availableDates = Array.from(
    new Set(
      commissioned
        .map((charge) => charge.availableAt)
        .filter((date): date is string => date !== undefined),
    ),
  ).sort()

  return {
    items,
    embeddedUncommissionedItems,
    totalPaid,
    totalGross,
    ...(commercialAmount !== undefined ? { commercialAmount, fxVariation } : {}),
    converted: totalPaid.currency !== totalGross.currency,
    deductions,
    fixedFeeCount: commissioned.every((charge) => charge.fixedFee?.currency === receiving)
      ? commissioned.filter((charge) => Number(charge.fixedFee?.amount) > 0).length
      : undefined,
    netTotal: netTotal.toMonetaryValue(),
    availableNow: availableNow.toMonetaryValue(),
    reserveHeld: reserveHeld.toMonetaryValue(),
    hasReserve: !reserveHeld.isZero(),
    debtConsumed: debtConsumed.toMonetaryValue(),
    remainingReceivable: remainingReceivable.toMonetaryValue(),
    remainingReserve: remainingReserve.toMonetaryValue(),
    hasDebtConsumption,
    availableDates,
    awaitingAvailability: commissioned.some(
      (charge) =>
        charge.availability?.status === 'unknown' ||
        (!charge.availableAt && charge.availability?.status !== 'available'),
    ),
    availabilityConfirmed:
      commissioned.length > 0 &&
      commissioned.every((charge) => charge.availability?.status === 'available'),
  }
}
